import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { IntegrationType } from '@/services/ws-header-tag/src/main/resources/rest-api';
import { CheckTypeInline } from '@/services/ws-operations/src/main/resources/rest-api';
import { CampaignPositionStatus } from '@/services/ws-print-operations/src/main/resources/rest-api';
import { Media } from '@/js/libs/ui';
import { Badge, Icons, PuzzleNav, RibbonVariant } from '@/js/libs/ui/src/V2';
import { PrivateContent, Roles, and, or, not } from '@/js/libs/use-auth';

import { ROUTER } from 'src/AppRouter';
import { DEV_MODE, MOBILE_HEADER_HEIGHT } from 'src/constants';
import CompaniesSwitch from 'src/modules/CompaniesSwitch';
import { settingsRouteRoles } from 'src/routes/settings';
import { DigitalOperationStatus } from 'src/scenes/DigitalOperations';
import { BroadcastUrlTypes } from 'src/scenes/Settings/containers/BroadcastProducts/types';
import { Footer, Header } from './components';
import { SearchInput } from './components/Header/Search';
import { useNavigationContext } from './context';
import { useNavigationComponentsVisibilityChecks } from './hooks/useNavigationComponentsVisibilityChecks';
import { useSelfBookingNavItems } from './hooks/useSelfBookingNavItems';

export const Navigation = memo(function Navigation() {
  // Please use only common and components namespaces here to avoid excessive namespaces loading
  const [t] = useTranslation(['common', 'components']);

  const { isGlobalSearchVisible, isCompanySwitchVisible } = useNavigationComponentsVisibilityChecks();
  const isDesktop = useMediaQuery({ query: Media.lgMin });

  const { isCollapsedNav, collapseMainNav, pendingArticlesCount, articlesCount } = useNavigationContext();

  const [needSearchAutofocus, setNeedSearchAutofocus] = useState(false);

  useEffect(() => {
    if (isCollapsedNav) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNeedSearchAutofocus(false);
    }
  }, [isCollapsedNav]);

  const { items: selfBookingNavItems, showIndicator: selfBookingShowIndicator } = useSelfBookingNavItems(t);

  return (
    <PuzzleNav
      header={isDesktop && <Header onNeedSearchAutofocus={setNeedSearchAutofocus} />}
      mobileHeaderHeight={MOBILE_HEADER_HEIGHT}
      searchInput={isGlobalSearchVisible && <SearchInput autofocus={needSearchAutofocus} />}
      companySelect={isCompanySwitchVisible && <CompaniesSwitch />}
      footer={<Footer isCollapsedNav={isCollapsedNav} />}
      isCollapsedNav={isCollapsedNav}
      collapseMainNav={collapseMainNav}>
      <PrivateContent roles={Roles.YALEO_SELF_BOOKING_USER}>
        {/* Legacy Self Booking, should be removed after migration */}
        <PrivateContent roles={Roles.SELF_BOOKING}>
          <PuzzleNav.ItemLink
            icon={<Icons.Document />}
            title={t('common:TITLE_CAMPAIGNS_MAIN')}
            to={ROUTER['/orders/campaigns'].path.pattern}
            path={ROUTER['/orders/campaigns'].path.pattern}
            dataTestId='campaigns-link-orders'
            ribbon={RibbonVariant.LEGACY}
          />
        </PrivateContent>
        {selfBookingNavItems.map(({ dataTestId, ...item }) => (
          <PuzzleNav.ItemLink key={dataTestId} dataTestId={dataTestId} {...item} />
        ))}
      </PrivateContent>

      <PrivateContent roles={and(Roles.YALEO_SELF_BOOKING, not(Roles.YALEO_SELF_BOOKING_USER))}>
        <PuzzleNav.Item
          icon={<Icons.Fire />}
          title={t('common:SELF_BOOKING')}
          itemKey='self-booking'
          path={ROUTER['/self-booking'].path.pattern}
          dataTestId='self-booking-category'
          showIndicator={selfBookingShowIndicator}
          ribbon={RibbonVariant.NEW}>
          {selfBookingNavItems.map(({ dataTestId, ...item }) => (
            <PuzzleNav.ItemSubLink key={dataTestId} dataTestId={dataTestId} {...item} />
          ))}
        </PuzzleNav.Item>
      </PrivateContent>

      <PrivateContent roles={not(Roles.YALEO_SELF_BOOKING_USER)}>
        <PrivateContent
          roles={and(
            or(Roles.MANAGE_ACCOUNTS, Roles.SELF_BOOKING),
            not(or(Roles.POSTADMANAGER, Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)),
          )}>
          <PuzzleNav.Item
            icon={<Icons.Users />}
            title={t('components:CRM')}
            itemKey='crm'
            path={ROUTER['/crm'].path.pattern}
            dataTestId='crm-category'>
            <PrivateContent roles={or(Roles.MANAGE_ACCOUNTS, Roles.SELF_BOOKING)}>
              <PuzzleNav.ItemSubLink title={t('common:ACCOUNTS')} to={ROUTER['/crm/accounts'].path.pattern} dataTestId='accounts-link' />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_ACCOUNTS}>
              <PuzzleNav.ItemSubLink title={t('common:CONTACTS')} to={ROUTER['/crm/contacts'].path.pattern} dataTestId='contacts-link' />
            </PrivateContent>
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={Roles.POSTADMANAGER}>
          <PuzzleNav.Item
            icon={<Icons.Users />}
            title={t('components:CRM')}
            itemKey='crm'
            path={ROUTER['/crm'].path.pattern}
            dataTestId='crm-category'>
            <PrivateContent roles={Roles.POSTADMANAGER}>
              <PuzzleNav.ItemSubLink
                title={t('common:ACCOUNTS')}
                to={ROUTER['/post-crm/accounts'].path.pattern}
                dataTestId='accounts-link'
              />
            </PrivateContent>
            <PuzzleNav.ItemSubLink title={t('common:CONTACTS')} to={ROUTER['/post-crm/contacts'].path.pattern} dataTestId='contacts-link' />
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={Roles.SALESFUNNEL}>
          <PuzzleNav.Item
            dataTestId='sales-category'
            icon={<Icons.Megaphone />}
            title={t('common:SALES')}
            itemKey='sales'
            path={ROUTER['/sales'].path.pattern}>
            <PuzzleNav.ItemSubLink
              title={t('common:TASKS')}
              to={ROUTER['/sales/tasks'].path({ STATUS: 'IN_PROGRESS' })}
              path={ROUTER['/sales/tasks'].path.pattern}
              dataTestId='tasks-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('common:ACTIVITIES')}
              to={ROUTER['/sales/activities'].path.pattern}
              path={[ROUTER['/sales/activities'].path.pattern, ROUTER['/sales/comments'].path.pattern]}
              dataTestId='activities-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('common:PROMOTIONS')}
              to={ROUTER['/sales/promotions'].path.pattern}
              dataTestId='promotions-link'
            />
            {DEV_MODE && (
              <PuzzleNav.ItemSubLink
                ribbon={RibbonVariant.NEW}
                title={t('common:PROMOTIONS')}
                to={ROUTER['/sales/promotions-v2'].path.pattern}
                dataTestId='promotions-link-v2'
              />
            )}
            <PuzzleNav.ItemSubLink
              title={t('common:BUTTON_SETTINGS')}
              path={[ROUTER['/sales/tags'].path.pattern, ROUTER['/sales/sources'].path.pattern, ROUTER['/sales/task-topics'].path.pattern]}
              to={ROUTER['/sales/tags'].path.pattern}
              dataTestId='sales-settings-link'
            />
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent
          roles={and(or(Roles.AGENCY, Roles.POSTADMANAGER), not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)))}>
          <PuzzleNav.Item
            icon={<Icons.Document />}
            title={t('common:ORDERS')}
            itemKey='orders'
            path={ROUTER['/orders'].path.pattern}
            dataTestId='orders-category'>
            <PrivateContent roles={Roles.POSTADMANAGER}>
              <PuzzleNav.ItemSubLink
                title={t('common:POST_CAMPAIGNS')}
                to={ROUTER['/orders/post-campaigns'].path.pattern}
                dataTestId='post-campaigns-link-orders'
              />
            </PrivateContent>
            <PrivateContent roles={not(Roles.POSTADMANAGER)}>
              <PuzzleNav.ItemSubLink
                title={t('common:TITLE_CAMPAIGNS_MAIN')}
                to={ROUTER['/orders/campaigns'].path.pattern}
                dataTestId='campaigns-link-orders'
              />
              <PrivateContent
                roles={or(
                  Roles.MANAGE_DIGITAL_PRODUCTS,
                  Roles.BOOK_CAMPAIGNS,
                  Roles.MANAGE_ACCOUNTS,
                  Roles.SELF_BOOKING,
                  Roles.VIEW_CAMPAIGNS,
                )}>
                <PuzzleNav.ItemSubLink
                  title={t('common:DIGITAL_POSITIONS')}
                  to={ROUTER['/orders/digital-positions'].path.pattern}
                  dataTestId='digital-positions-link-orders'
                />
              </PrivateContent>
              <PrivateContent roles={Roles.MANAGE_PRINT_PRODUCTS}>
                <PuzzleNav.ItemSubLink
                  title={t('common:PRINT_POSITIONS')}
                  to={ROUTER['/orders/print-positions'].path.pattern}
                  dataTestId='print-positions-link-orders'
                />
              </PrivateContent>
              <PrivateContent roles={Roles.MANAGE_RADIO_PRODUCTS}>
                <PuzzleNav.ItemSubLink
                  title={t('common:RADIO_POSITIONS')}
                  to={ROUTER['/orders/:broadcastType'].path({ broadcastType: 'radio-positions' })}
                  dataTestId='radio-positions-link-orders'
                />
              </PrivateContent>
              <PrivateContent roles={Roles.MANAGE_TV_PRODUCTS}>
                <PuzzleNav.ItemSubLink
                  title={t('common:TV_POSITIONS')}
                  to={ROUTER['/orders/:broadcastType'].path({ broadcastType: 'tv-positions' })}
                  dataTestId='tv-positions-link-orders'
                />
              </PrivateContent>
              <PrivateContent roles={Roles.MANAGE_POS_PRODUCTS}>
                <PuzzleNav.ItemSubLink
                  title={t('common:POINT_OF_SALE_POSITIONS')}
                  to={ROUTER['/orders/point-of-sale-positions'].path.pattern}
                  dataTestId='point-of-sale-positions-link-orders'
                />
              </PrivateContent>
              <PrivateContent roles={Roles.ADMIN}>
                <PuzzleNav.ItemSubLink
                  title={t('components:PROGRAMMATIC_DEALS')}
                  to={ROUTER['/orders/programmatic-deals'].path.pattern}
                  dataTestId='programmatic-deals-link'
                />
              </PrivateContent>
              <PrivateContent roles={or(Roles.ADMIN, Roles.BOOK_CAMPAIGNS)}>
                <PuzzleNav.ItemSubLink
                  title={`${t('common:BOOKING_TITLE')}`}
                  to={ROUTER['/orders/booking'].path.pattern}
                  dataTestId='booking-calendar-link'
                />
              </PrivateContent>
              <PrivateContent roles={Roles.MANAGE_DIGITAL_PRODUCTS}>
                <PuzzleNav.ItemSubLink
                  title={t('common:FORECAST')}
                  to={ROUTER['/orders/forecast'].path.pattern}
                  dataTestId='forecast-link'
                />
              </PrivateContent>
            </PrivateContent>
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)}>
          <PuzzleNav.Item
            icon={<Icons.Document />}
            title={t('common:ORDERS')}
            itemKey='orders'
            path={ROUTER['/orders'].path.pattern}
            dataTestId='orders-category'>
            <PuzzleNav.ItemSubLink
              title={`${t('common:BOOKING_TITLE')}`}
              to={ROUTER['/orders/booking'].path.pattern}
              dataTestId='booking-calendar-link'
            />
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent
          roles={or(
            Roles.SELF_BOOKING,
            Roles.ADSERVER_CREATIVES,
            Roles.MANAGE_DIGITAL_CREATIVES,
            Roles.MANAGE_PRINT_CREATIVES,
            Roles.ADMIN,
          )}>
          <PuzzleNav.Item
            itemKey='creatives'
            icon={<Icons.Photo />}
            title={t('common:CREATIVES')}
            path={ROUTER['/creatives'].path.pattern}
            dataTestId='creatives-category'>
            <PrivateContent roles={Roles.ADSERVER_CREATIVES}>
              <PuzzleNav.ItemSubLink
                title={t('common:ADSERVER_CREATIVES')}
                to={ROUTER['/creatives/ad-server'].path.pattern}
                path={ROUTER['/creatives/ad-server'].path.pattern}
                dataTestId='adserver-creatives-link'
              />
            </PrivateContent>
            <PrivateContent roles={or(Roles.MANAGE_DIGITAL_CREATIVES, Roles.SELF_BOOKING)}>
              <PuzzleNav.ItemSubLink
                title={t('common:DIGITAL_CREATIVES')}
                to={ROUTER['/creatives/digital'].path.pattern}
                path={ROUTER['/creatives/digital'].path.pattern}
                dataTestId='digital-creatives-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_PRINT_CREATIVES}>
              <PuzzleNav.ItemSubLink
                title={t('common:PRINT_CREATIVES')}
                to={ROUTER['/creatives/print'].path.pattern}
                path={ROUTER['/creatives/print'].path.pattern}
                dataTestId='print-creatives-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('common:TEMPLATES')}
                to={ROUTER['/creatives/templates'].path.pattern}
                path={ROUTER['/creatives/templates'].path.pattern}
                dataTestId='templates-link'
                ribbon={RibbonVariant.BETA}
              />
            </PrivateContent>
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent
          roles={or(and(Roles.MANAGE_DIGITAL_PRODUCTS, or(Roles.OPS_PUSH, Roles.OPS_SCREENSHOTS, Roles.OPS_CHECKS)), Roles.ADMIN)}>
          <PuzzleNav.Item
            icon={<Icons.Display />}
            title={t('common:OPERATIONS')}
            itemKey='operations'
            path={ROUTER['/digital-operations'].path.pattern}
            dataTestId='digital-operations-category'>
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('components:PN_AD_APPROVAL')}
                to={ROUTER['/digital-operations/approval/:status'].path({ status: DigitalOperationStatus.PENDING_APPROVAL })}
                path={ROUTER['/digital-operations/approval'].path.pattern}
                dataTestId='ad-approval-link'
                tag={!pendingArticlesCount && !!articlesCount ? <Badge informative xs title={articlesCount.toString()} /> : undefined}
              />
            </PrivateContent>
            <PrivateContent roles={Roles.OPS_PUSH}>
              <PuzzleNav.ItemSubLink
                title={t('components:PN_CAMPAIGN_MANAGEMENT')}
                to={ROUTER['/digital-operations/campaign-management/:status'].path({ status: DigitalOperationStatus.READY_TO_PUSH })}
                path={ROUTER['/digital-operations/campaign-management'].path.pattern}
                dataTestId='campaign-management-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.OPS_SCREENSHOTS}>
              <PuzzleNav.ItemSubLink
                title={t('components:PN_SCREENSHOT_MANAGEMENT')}
                to={ROUTER['/digital-operations/screenshot-management'].path.pattern}
                dataTestId='screenshots-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.OPS_CHECKS}>
              <PuzzleNav.ItemSubLink
                title={t('components:PN_QUALITY_ASSURANCE')}
                to={ROUTER['/digital-operations/quality-assurance/:checkType'].path({ checkType: CheckTypeInline.START })}
                path={ROUTER['/digital-operations/quality-assurance'].path.pattern}
                dataTestId='quality-assurance-link'
              />
              {DEV_MODE && (
                <PuzzleNav.ItemSubLink
                  ribbon={RibbonVariant.NEW}
                  title={t('components:PN_QUALITY_ASSURANCE')}
                  to={ROUTER['/digital-operations/quality-assurance2'].path.pattern}
                  path={ROUTER['/digital-operations/quality-assurance2'].path.pattern}
                  dataTestId='quality-assurance-link-new'
                />
              )}
            </PrivateContent>
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={or(Roles.POSTNETZ, Roles.POSTNETZ_BRANCH_USER)}>
          <PuzzleNav.Item
            icon={<Icons.Newspaper />}
            title={t('common:ANALOG_OPERATIONS')}
            itemKey='analog-operations'
            path={ROUTER['/analog-operations'].path.pattern}
            dataTestId='analog-operations-category'>
            <PrivateContent roles={Roles.POSTNETZ}>
              <PuzzleNav.ItemSubLink
                title={t('components:PN_CAMPAIGN_MANAGEMENT')}
                to={ROUTER['/analog-operations/campaign-management'].path.pattern}
                path={ROUTER['/analog-operations/campaign-management'].path.pattern}
                dataTestId='analog-operations-campaign-management-link'
              />
            </PrivateContent>
            <PuzzleNav.ItemSubLink
              title={t('common:INVENTORY_MANAGEMENT')}
              to={ROUTER['/analog-operations/inventory-management'].path.pattern}
              path={ROUTER['/analog-operations/inventory-management'].path.pattern}
              dataTestId='analog-operations-inventory-management-link'
            />
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={and(Roles.MANAGE_PRINT_PRODUCTS, Roles.PRINT_OPS_PUSH)}>
          <PuzzleNav.Item
            icon={<Icons.Newspaper />}
            title={t('components:PRINT_OPERATIONS')}
            itemKey='printoperations'
            path={ROUTER['/print-operations'].path.pattern}
            dataTestId='print-operations-category'>
            <PuzzleNav.ItemSubLink
              title={t('common:PRINT')}
              to={ROUTER['/print-operations/print/:status'].path({ status: CampaignPositionStatus.READY_TO_PUSH })}
              path={ROUTER['/print-operations/print'].path.pattern}
              dataTestId='print-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('common:PUBLICATION_UPLOAD')}
              exact
              to={ROUTER['/print-operations/publication-upload'].path.pattern}
              dataTestId='print-publication-upload-link'
            />
          </PuzzleNav.Item>
        </PrivateContent>
        {DEV_MODE && (
          <PrivateContent roles={and(Roles.MANAGE_PRINT_PRODUCTS, Roles.PRINT_OPS_PUSH)}>
            <PuzzleNav.Item
              ribbon={RibbonVariant.NEW}
              icon={<Icons.Newspaper />}
              title={t('components:PRINT_OPERATIONS')}
              itemKey='printoperationsnew'
              path={ROUTER['/print-operations-new'].path.pattern}>
              <PuzzleNav.ItemSubLink
                title={t('common:PRINT')}
                to={ROUTER['/print-operations-new/print'].path({ activeTabId: CampaignPositionStatus.READY_TO_PUSH })}
                path={ROUTER['/print-operations-new/print'].path.pattern}
              />
              <PuzzleNav.ItemSubLink
                title={t('common:PUBLICATION_UPLOAD')}
                exact
                to={ROUTER['/print-operations/publication-upload'].path.pattern}
                dataTestId='print-publication-upload-link'
              />
            </PuzzleNav.Item>
          </PrivateContent>
        )}
        <PrivateContent
          roles={and(
            or(Roles.ADMIN_INVOICE, Roles.ADMIN, Roles.PAYOUT, Roles.POSTADMANAGER, Roles.PAYMENTS),
            not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER)),
          )}>
          <PuzzleNav.Item
            icon={<Icons.Dollar />}
            title={t('components:FINANCES')}
            itemKey='finance'
            path={ROUTER['/finances'].path.pattern}
            dataTestId='finances-category'>
            <PrivateContent roles={Roles.ADMIN_INVOICE}>
              <PuzzleNav.ItemSubLink
                title={t('common:INVOICES_TITLE')}
                to={ROUTER['/finances/invoices'].path.pattern}
                dataTestId='invoicing-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.POSTADMANAGER}>
              <PuzzleNav.ItemSubLink
                title={t('common:INVOICES_TITLE')}
                to={ROUTER['/finances/post-invoices'].path.pattern}
                dataTestId='post-invoicing-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('common:COMPANY_EXPORTS_TITLE')}
                to={ROUTER['/finances/company-configs'].path.pattern}
                dataTestId='company-configs-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.PAYOUT}>
              <PuzzleNav.ItemSubLink
                title={t('common:PAYOUTS')}
                to={ROUTER['/finances/payouts/reports'].path.pattern}
                path={ROUTER['/finances/payouts'].path.pattern}
                dataTestId='payouts-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('common:RECEIPTS')}
                to={ROUTER['/finances/credit-card-receipts'].path.pattern}
                dataTestId='credit-card-receipts-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.PAYMENTS}>
              <PuzzleNav.ItemSubLink
                title={t('components:PAYMENTS_TITLE')}
                to={ROUTER['/finances/payments'].path.pattern}
                dataTestId='payments-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('components:BOOKING_PERIODS_TITLE')}
                to={ROUTER['/finances/booking-periods'].path.pattern}
                dataTestId='booking-periods-link'
              />
            </PrivateContent>
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent
          roles={or(
            Roles.ADMIN,
            and(Roles.REPORT_BUILDER, Roles.BTA_FEED_REPORT_BUILDER),
            and(Roles.REPORT_BUILDER, not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER))),
          )}>
          <PuzzleNav.Item
            icon={<Icons.Chart />}
            title={t('components:BUSINESS_INTELLIGENCE')}
            itemKey='business-intelligence'
            path={ROUTER['/business-intelligence'].path.pattern}
            dataTestId='business-intelligence-category'>
            <PuzzleNav.ItemSubLink
              title={t('common:REPORTING')}
              to={ROUTER['/business-intelligence/reporting'].path.pattern}
              dataTestId='reporting-link'
            />
            {DEV_MODE && (
              <>
                <PuzzleNav.ItemSubLink
                  title={t('common:REPORTING')}
                  to={ROUTER['/business-intelligence/reporting-v2'].path.pattern}
                  dataTestId='reporting-link-v2'
                  ribbon={RibbonVariant.NEW}
                />
                <PuzzleNav.ItemSubLink
                  title={t('common:DASHBOARDS')}
                  to={ROUTER['/business-intelligence/visualizations'].path.pattern}
                  dataTestId='visualizations-link'
                />
              </>
            )}
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={Roles.ANALYTICS_TOOL}>
          <PuzzleNav.Item
            icon={<Icons.Analytics />}
            title={t('components:ANALYTICS_TOOL')}
            itemKey='analytics-tool'
            path={ROUTER['/analytics-tool'].path.pattern}
            dataTestId='analytics-tool-category'>
            <PuzzleNav.ItemSubLink
              title={t('components:ANALYTICS_TOOL_PAGE_IMPRESSIONS')}
              to={ROUTER['/analytics-tool/page-impressions'].path.pattern}
              dataTestId='analytics-tool-page-impressions-link'
              ribbon={RibbonVariant.BETA}
            />
            <PuzzleNav.ItemSubLink
              title={t('components:ANALYTICS_TOOL_AD_IMPRESSIONS')}
              to={ROUTER['/analytics-tool/ad-impressions'].path.pattern}
              dataTestId='analytics-tool-ad-impressions-link'
              ribbon={RibbonVariant.BETA}
            />
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={Roles.SEMANTIQ}>
          <PuzzleNav.Item
            icon={<Icons.Semantiq />}
            title={t('components:SEMANTIQ')}
            itemKey='semantiq'
            path={ROUTER['/semantiq'].path.pattern}
            dataTestId='semantiq-category'>
            <PuzzleNav.ItemSubLink
              title={t('common:DASHBOARD')}
              to={ROUTER['/semantiq/dashboard'].path.pattern}
              dataTestId='dashboard-link-semantiq'
            />
            <PuzzleNav.ItemSubLink
              title={t('common:CUSTOM_AUDIENCES_TITLE')}
              to={ROUTER['/semantiq/custom-audiences'].path.pattern}
              dataTestId='custom-audiences-link-semantiq'
            />
            <PuzzleNav.ItemSubLink
              title={t('common:IAB_AUDIENCES_TITLE')}
              to={ROUTER['/semantiq/iab-audiences'].path.pattern}
              dataTestId='iab-audiences-link-semantiq'
            />
            <PuzzleNav.ItemSubLink
              title={t('common:DOMAINS_TITLE')}
              to={ROUTER['/semantiq/domains'].path.pattern}
              dataTestId='domains-link-semantiq'
            />
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent
          roles={or(Roles.ADMIN, and(Roles.PUBLISHER, not(or(Roles.POSTNETZ_BRANCH_USER, Roles.READ_ONLY_POSTNETZ_BRANCH_USER))))}>
          <PuzzleNav.Item
            dataTestId='publishers-category'
            icon={<Icons.RSS />}
            title={t('components:PUBLISHERS')}
            itemKey='publishers'
            path={ROUTER['/publishers'].path.pattern}>
            <PrivateContent roles={Roles.PUBLISHER}>
              <>
                <PuzzleNav.ItemSubLink
                  title={t('components:PUBLISHER_OVERVIEW')}
                  to={ROUTER['/publishers/overview'].path.pattern}
                  dataTestId='overview-link'
                />
                <PuzzleNav.ItemSubLink
                  title={t('components:PUBLISHER_CAMPAIGNS')}
                  to={ROUTER['/publishers/campaigns'].path.pattern}
                  dataTestId='campaigns-link-publishers'
                />
                <PuzzleNav.ItemSubLink
                  title={t('components:PUBLISHER_PAYOUTS')}
                  to={ROUTER['/publishers/payouts'].path.pattern}
                  dataTestId='payouts-link-publishers'
                />
                <PuzzleNav.ItemSubLink
                  title={t('components:EXCLUSIONS')}
                  to={ROUTER['/publishers/exclusions'].path.pattern}
                  dataTestId='exclusions-link-publishers'
                />
                <PrivateContent roles={Roles.ADMIN}>
                  <PuzzleNav.ItemSubLink
                    title={t('components:TRAFFIC')}
                    to={ROUTER['/publishers/traffic'].path.pattern}
                    dataTestId='traffic-link-publishers'
                  />
                </PrivateContent>
              </>
            </PrivateContent>
            <PrivateContent roles={Roles.CONFIGURE_ADS_TXT}>
              <PuzzleNav.ItemSubLink
                title={t('components:ADS_CONFIG')}
                to={ROUTER['/publishers/ads-config'].path.pattern}
                dataTestId='ads-config-link'
              />
            </PrivateContent>
            {DEV_MODE && (
              <PrivateContent roles={Roles.ADMIN}>
                <PuzzleNav.ItemSubLink
                  ribbon={RibbonVariant.NEW}
                  title={t('components:ADS_CONFIG_TITLE')}
                  to={ROUTER['/publishers/ads-config-v2/configs'].path.pattern}
                  dataTestId='ads-config-link-v2'
                />
              </PrivateContent>
            )}
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={Roles.ADMIN}>
          <PuzzleNav.Item
            dataTestId='gen-ai-link'
            icon={<Icons.AiAssistant />}
            title={t('components:GEN_AI')}
            itemKey='gen-ai'
            path={ROUTER['/gen-ai'].path.pattern}>
            <PuzzleNav.ItemSubLink
              title={t('components:GEN_AI_CHAT')}
              to={ROUTER['/gen-ai/chat'].path.pattern}
              dataTestId='gen-ai-link-chat'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:GEN_AI_IMAGE')}
              to={ROUTER['/gen-ai/image'].path.pattern}
              dataTestId='gen-ai-link-image'
            />
            {DEV_MODE && (
              <PuzzleNav.ItemSubLink
                title={t('components:GEN_AI_VIDEO')}
                to={ROUTER['/gen-ai/video'].path.pattern}
                dataTestId='gen-ai-link-video'
              />
            )}
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={Roles.ADMIN}>
          <PuzzleNav.Item
            dataTestId='ad-tags-category'
            icon={<Icons.Tag />}
            title={t('common:TITLE_AD_TAGS')}
            itemKey='ad-tags'
            path={ROUTER['/ad-tags'].path.pattern}>
            <PuzzleNav.ItemSubLink
              title={t('components:FIRST_PARTY_WEBSITE')}
              to={ROUTER['/ad-tags/1st-party/tags'].path.pattern}
              dataTestId='1st-party-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:THIRD_PARTY_WEBSITE')}
              to={ROUTER['/ad-tags/3rd-party'].path.pattern}
              dataTestId='3rd-party-link'
            />
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={or(Roles.ADMIN, Roles.HEADER_TAG)}>
          <PuzzleNav.Item
            dataTestId='header-tag-category'
            icon={<Icons.Tag />}
            title={t('components:HEADER_TAG')}
            itemKey='header-tag'
            path={[
              ROUTER['/header-tag/tags/:integrationType'].path({ integrationType: IntegrationType.CLIENT }),
              ROUTER['/header-tag/raw-scripts'].path.pattern,
              ROUTER['/header-tag/floor-rules'].path.pattern,
              ROUTER['/header-tag/bidders-configs'].path.pattern,
              ROUTER['/header-tag/bid-reduction'].path.pattern,
            ]}>
            <PuzzleNav.ItemSubLink
              title={t('common:TAGS')}
              to={ROUTER['/header-tag/tags/:integrationType'].path({ integrationType: IntegrationType.CLIENT })}
              dataTestId='header-tag-tags-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:RAW_SCRIPTS')}
              to={ROUTER['/header-tag/raw-scripts'].path.pattern}
              dataTestId='header-tag-raw-scripts-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:FLOOR_RULES')}
              to={ROUTER['/header-tag/floor-rules'].path.pattern}
              dataTestId='header-tag-floor-rules-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:BIDDERS_CONFIGS')}
              to={ROUTER['/header-tag/bidders-configs'].path.pattern}
              dataTestId='header-tag-bidders-configs-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:BID_REDUCTION')}
              to={ROUTER['/header-tag/bid-reduction'].path.pattern}
              dataTestId='header-tag-bid-reduction-link'
              ribbon={RibbonVariant.NEW}
            />
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={or(Roles.ADMIN, Roles.HEADER_TAG)}>
          <PuzzleNav.Item
            dataTestId='placement-configs-category'
            icon={<Icons.Development />}
            title={t('components:PLACEMENT_CONFIGS')}
            itemKey='placement-configs'
            path={[
              ROUTER['/placement-configs'].path.pattern,
              ROUTER['/header-tag/tags/:integrationType'].path({ integrationType: IntegrationType.SERVER }),
            ]}>
            <PuzzleNav.ItemSubLink
              title={t('components:APP_CONFIGS')}
              to={ROUTER['/placement-configs/app-configs'].path.pattern}
              dataTestId='placement-configs-app-configs-link'
            />
            {DEV_MODE && (
              <PuzzleNav.ItemSubLink
                ribbon={RibbonVariant.NEW}
                title={t('components:PLACEMENT_PUSH_CONFIG')}
                to={ROUTER['/header-tag/tags/:integrationType'].path({ integrationType: IntegrationType.SERVER })}
                dataTestId='placement-configs-placement-push-config-link'
              />
            )}
            {DEV_MODE && (
              <PrivateContent roles={Roles.ADMIN}>
                <PuzzleNav.ItemSubLink
                  ribbon={RibbonVariant.NEW}
                  title={t('components:THIRD_PARTY_WEBSITES')}
                  to={ROUTER['/placement-configs/third-party-websites'].path.pattern}
                  dataTestId='placement-configs-third-party-websites-link'
                />
              </PrivateContent>
            )}
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={Roles.YALEO}>
          <PuzzleNav.Item
            icon={<Icons.Yaleo />}
            title={t('common:YALEO')}
            itemKey='yaleo'
            path={ROUTER['/yaleo'].path.pattern}
            dataTestId='yaleo-category'>
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('components:CRAWLER_CONFIGS')}
                to={ROUTER['/yaleo/crawler-configuration'].path.pattern}
                dataTestId='yaleo-crawler-configuration-link'
              />
            </PrivateContent>
            <PuzzleNav.ItemSubLink
              title={t('common:PLACEMENTS')}
              to={ROUTER['/yaleo/placements'].path.pattern}
              dataTestId='yaleo-placements-link'
            />
            {DEV_MODE && (
              <PrivateContent roles={Roles.ADMIN}>
                <PuzzleNav.ItemSubLink
                  title={t('common:PLACEMENTS')}
                  to={ROUTER['/yaleo/placements-v2'].path.pattern}
                  dataTestId='yaleo-placements-link-v2'
                  ribbon={RibbonVariant.BETA}
                />
              </PrivateContent>
            )}
            <PuzzleNav.ItemSubLink
              title={t('components:BTA_FEED')}
              to={ROUTER['/yaleo/bta-feed/websites'].path.pattern}
              dataTestId='bta-feed-link'
            />
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('components:BTA_FEED')}
                to={ROUTER['/yaleo/bta-feed/v2'].path.pattern}
                dataTestId='bta-feed-link-v2'
                ribbon={RibbonVariant.BETA}
              />
            </PrivateContent>
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('components:WEBSITES_ALGORITHMS_CONFIG')}
                to={ROUTER['/yaleo/algorithms-config'].path.pattern}
                dataTestId='algorithms-config-link'
              />
            </PrivateContent>
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={Roles.ADMIN}>
          <PuzzleNav.Item
            icon={<Icons.AdConsole size={16} />}
            title={t('components:ADMIN_TITLE')}
            itemKey='admin'
            path={ROUTER['/admin'].path.pattern}
            dataTestId='admin-category'>
            <PuzzleNav.ItemSubLink title={t('common:ADMIN_USERS')} to={ROUTER['/admin/users'].path.pattern} dataTestId='users-link' />
            <PuzzleNav.ItemSubLink title={t('common:COMPANIES')} to={ROUTER['/admin/companies'].path.pattern} dataTestId='companies-link' />
            <PuzzleNav.ItemSubLink title={t('common:EMAILS')} to={ROUTER['/admin/mail'].path.pattern} dataTestId='admin-mail-link' />
            <PuzzleNav.ItemSubLink
              title={t('common:NOTIFICATIONS')}
              to={ROUTER['/admin/notifications'].path.pattern}
              dataTestId='notifications-link'
            />
            <PuzzleNav.ItemSubLink
              ribbon={RibbonVariant.NEW}
              title={t('common:WEBSITE_MAPPINGS')}
              to={ROUTER['/admin/website-mapping'].path.pattern}
              dataTestId='website-mappings-link'
            />
            <PuzzleNav.ItemSubLink
              ribbon={RibbonVariant.NEW}
              title={t('common:WEBSITE_GROUPS')}
              to={ROUTER['/admin/website-groups'].path.pattern}
              dataTestId='website-groups-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:LOCALIZATION')}
              to={ROUTER['/admin/localization'].path.pattern}
              dataTestId='localization-link-v2'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:ADMIN_BATCH')}
              to={ROUTER['/admin/batch'].path.pattern}
              dataTestId='admin-batch-link'
            />
            <PuzzleNav.ItemSubLink
              title={`${t('components:ADMIN_BATCH')}-k8s`}
              to={ROUTER['/admin/batch-k8s'].path.pattern}
              dataTestId='admin-batch-k8s-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:XANDR_CREDENTIALS')}
              to={ROUTER['/admin/xandr-credentials'].path.pattern}
              dataTestId='xandr-credentials-link'
            />
            <PuzzleNav.ItemSubLink
              title={t('components:XANDR_SEGMENTS')}
              to={ROUTER['/admin/xandr-segments'].path.pattern}
              dataTestId='xandr-segments-link'
            />
            <PuzzleNav.ItemSubLink dataTestId='permalead-link' title={t('common:PERMALEAD')} to={ROUTER['/admin/permalead'].path.pattern} />
          </PuzzleNav.Item>
        </PrivateContent>

        <PrivateContent roles={Roles.ACCESS_COMPLIANCE}>
          <PuzzleNav.ItemLink
            icon={<Icons.Compliance />}
            title={t('common:ADMIN_COMPLIANCE')}
            path={ROUTER['/compliance'].path.pattern}
            to={ROUTER['/compliance'].path.pattern}
            dataTestId='compliance'
          />
        </PrivateContent>

        <PrivateContent roles={settingsRouteRoles}>
          <PuzzleNav.Item
            icon={<Icons.Gear />}
            title={t('common:BUTTON_SETTINGS')}
            itemKey='settings'
            path={ROUTER['/settings'].path.pattern}
            dataTestId='settings-category'>
            <PrivateContent roles={and(Roles.MANAGE_ACCOUNTS, not(Roles.POSTADMANAGER))}>
              <PuzzleNav.ItemSubLink
                title={t('common:DATA_IMPORT')}
                to={ROUTER['/settings/data-import'].path.pattern}
                dataTestId='data-import-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.POSTNETZ}>
              <PuzzleNav.ItemSubLink
                title={t('common:PARTNER_MANAGEMENT')}
                to={ROUTER['/settings/partner-filial-management'].path.pattern}
                dataTestId='partner-filial-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.POSTNETZ}>
              <PuzzleNav.ItemSubLink
                title={t('components:ANALOG_PRODUCTS')}
                to={ROUTER['/settings/analog-products'].path.pattern}
                dataTestId='analog-products-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_DIGITAL_PRODUCTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:DIGITAL_PRODUCTS')}
                to={ROUTER['/settings/digital-products'].path.pattern}
                dataTestId='digital-products-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_RADIO_PRODUCTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:RADIO_PRODUCTS')}
                to={ROUTER['/settings/:broadcastType'].path({ broadcastType: BroadcastUrlTypes.RADIO })}
                dataTestId='radio-products-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_TV_PRODUCTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:MANAGE_TV_PRODUCTS')}
                to={ROUTER['/settings/:broadcastType'].path({ broadcastType: BroadcastUrlTypes.TV })}
                dataTestId='tv-products-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_PRINT_PRODUCTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:PRINT_PRODUCTS')}
                to={ROUTER['/settings/print-products'].path.pattern}
                dataTestId='print-products-link2'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_POS_PRODUCTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:POINT_OF_SALE_PRODUCTS')}
                to={ROUTER['/settings/point-of-sale-products'].path.pattern}
                dataTestId='point-of-sale-products-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_CONSULTING_PRODUCTS}>
              <PuzzleNav.ItemSubLink
                ribbon={RibbonVariant.NEW}
                title={t('common:CONSULTING_PRODUCTS')}
                to={ROUTER['/settings/consulting-products'].path.pattern}
                dataTestId='consulting-products-link'
              />
            </PrivateContent>
            <PrivateContent roles={or(Roles.MANAGE_DIGITAL_PRODUCTS, Roles.MANAGE_PRINT_PRODUCTS, Roles.MANAGE_POS_PRODUCTS)}>
              <PuzzleNav.ItemSubLink
                title={t('common:POSITION_GROUPS')}
                to={ROUTER['/settings/position-groups'].path.pattern}
                dataTestId='position-groups-link'
              />
            </PrivateContent>
            <PrivateContent
              roles={or(
                Roles.MANAGE_DIGITAL_PRODUCTS,
                Roles.MANAGE_PRINT_PRODUCTS,
                Roles.MANAGE_TV_PRODUCTS,
                Roles.MANAGE_RADIO_PRODUCTS,
                Roles.MANAGE_POS_PRODUCTS,
              )}>
              <PuzzleNav.ItemSubLink
                title={t('common:PRICE_LISTS')}
                to={ROUTER['/settings/price-lists'].path.pattern}
                dataTestId='price-lists-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.BOOK_CAMPAIGNS}>
              <PuzzleNav.ItemSubLink
                title={t('common:SALES_UNITS')}
                to={ROUTER['/settings/sales-units'].path.pattern}
                dataTestId='sales-units-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.PAYOUT}>
              <PuzzleNav.ItemSubLink
                title={t('common:WEBSITES')}
                to={ROUTER['/settings/websites'].path.pattern}
                dataTestId='websites-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.ADMIN}>
              <PuzzleNav.ItemSubLink
                title={t('common:BRANDS_TITLE')}
                to={ROUTER['/settings/brands'].path.pattern}
                dataTestId='brands-link'
              />
            </PrivateContent>
            <PrivateContent roles={or(Roles.ADMIN, Roles.PAYOUT)}>
              <>
                <PuzzleNav.ItemSubLink
                  title={t('components:TITLE_XANDR_MANAGEMENT')}
                  to={ROUTER['/settings/xandr-programmatic'].path.pattern}
                  path={ROUTER['/settings/xandr-programmatic'].path.pattern}
                  dataTestId='xandr-programmatic-link'
                />
                <PuzzleNav.ItemSubLink
                  title={t('components:TITLE_GOOGLE_MANAGEMENT')}
                  to={ROUTER['/settings/google-programmatic'].path.pattern}
                  path={ROUTER['/settings/google-programmatic'].path.pattern}
                  dataTestId='google-programmatic-link'
                />
                <PuzzleNav.ItemSubLink
                  title={t('components:TITLE_ADOMIK_MANAGEMENT')}
                  to={ROUTER['/settings/adomik-programmatic'].path.pattern}
                  path={ROUTER['/settings/adomik-programmatic'].path.pattern}
                  dataTestId='adomik-programmatic-link'
                />
              </>
            </PrivateContent>
            <PrivateContent roles={Roles.ADMIN}>
              <>
                <PuzzleNav.ItemSubLink
                  title={t('components:TITLE_PASSENDO')}
                  to={ROUTER['/settings/passendo/websites-mapping'].path.pattern}
                  dataTestId='passendo-link'
                />
                <PuzzleNav.ItemSubLink
                  title={t('components:TITLE_ADSENSE_MAPPINGS')}
                  to={ROUTER['/settings/adsense/mappings'].path.pattern}
                  path={ROUTER['/settings/adsense'].path.pattern}
                  dataTestId='ad-sense-mappings-link'
                />
              </>
            </PrivateContent>
            <PrivateContent roles={or(Roles.BOOK_CAMPAIGNS, Roles.POSTADMANAGER)}>
              <PuzzleNav.ItemSubLink
                title={t('components:COMPANY_SETTINGS')}
                to={ROUTER['/settings/company-settings'].path.pattern}
                dataTestId='company-settings-link'
              />
            </PrivateContent>
            <PrivateContent roles={or(Roles.MANAGE_ACCOUNTS, Roles.POSTADMANAGER)}>
              <PuzzleNav.ItemSubLink
                title={t('common:INDUSTRIES')}
                to={ROUTER['/settings/industries'].path.pattern}
                dataTestId='industries-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_ACCOUNTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:ACCOUNT_TYPES')}
                to={ROUTER['/settings/account-types'].path.pattern}
                dataTestId='account-types-link'
                ribbon={RibbonVariant.NEW}
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_CAMPAIGN_TYPES}>
              <PuzzleNav.ItemSubLink
                title={t('components:CAMPAIGN_TYPES')}
                to={ROUTER['/settings/campaign-types'].path.pattern}
                dataTestId='campaign-types-link'
              />
              {DEV_MODE && (
                <PuzzleNav.ItemSubLink
                  title={`${t('components:CAMPAIGN_TYPES')}`}
                  to={ROUTER['/settings/campaign-types-v2'].path.pattern}
                  dataTestId='campaign-types-v2-link'
                  ribbon={RibbonVariant.NEW}
                />
              )}
            </PrivateContent>
            <PrivateContent roles={and(Roles.REPORT_BUILDER, or(Roles.BOOK_CAMPAIGNS, Roles.SALESFUNNEL, Roles.POSTADMANAGER))}>
              <PuzzleNav.ItemSubLink
                title={t('common:LABELS')}
                to={ROUTER['/settings/labels'].path.pattern}
                dataTestId='admin-labels-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.MANAGE_REFERENCE_PER_AD_MEDIA}>
              <PuzzleNav.ItemSubLink
                title={t('common:REFERENCE_PER_ADMEDIA')}
                to={ROUTER['/settings/reference-per-admedia'].path.pattern}
                dataTestId='reference-per-admedia-link'
              />
            </PrivateContent>
            <PrivateContent roles={Roles.SPECIAL_DISCOUNTS}>
              <PuzzleNav.ItemSubLink
                title={t('common:SPECIAL_DISCOUNTS')}
                to={ROUTER['/settings/special-discounts'].path.pattern}
                dataTestId='special-discounts-link'
                ribbon={RibbonVariant.NEW}
              />
            </PrivateContent>
          </PuzzleNav.Item>
        </PrivateContent>
      </PrivateContent>
    </PuzzleNav>
  );
});
